import flowerImage from "@assets/images/flower.png";
import { useUpdateOnboardingUser } from "@features/onboarding/mutations/useUpdateOnboardingUser";
import { zodResolver } from "@hookform/resolvers/zod";
import logger from "@logger";
import { isAtLeast18YearsOld } from "@schemas/users.validators";
import Container from "@shared/components/Container";
import { useCurrentUser } from "@shared/queries";
import { calculateAge } from "@shared/utils/ageUtils";
import clsx from "clsx";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import DateOfBirthField from "./date-picker/DateOfBirthField";

// Validation schema for birthday (input validation only)
const birthdayInputSchema = z.object({
  date_of_birth: z
    .string({
      error: "Please enter your birthday",
    })
    .refine(
      (dateString) => {
        // Validate date string format and actual date validity
        if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return false;
        }

        const dateParts = dateString.split("-");
        if (dateParts.length !== 3) {
          return false;
        }

        const yearStr = dateParts[0];
        const monthStr = dateParts[1];
        const dayStr = dateParts[2];

        if (!yearStr || !monthStr || !dayStr) {
          return false;
        }

        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-indexed
        const day = parseInt(dayStr, 10);

        if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
          return false;
        }

        // Create date and check if it matches input values
        // (catches invalid dates like February 30th)
        const date = new Date(year, month, day);
        return (
          date instanceof Date &&
          !Number.isNaN(date.getTime()) &&
          date.getFullYear() === year &&
          date.getMonth() === month &&
          date.getDate() === day
        );
      },
      {
        message: "Please enter a valid date",
      }
    )
    .transform((dateString) => {
      // Parse YYYY-MM-DD format
      const dateParts = dateString.split("-");
      if (dateParts.length !== 3) {
        throw new Error("Invalid date format");
      }

      const yearStr = dateParts[0];
      const monthStr = dateParts[1];
      const dayStr = dateParts[2];

      if (!yearStr || !monthStr || !dayStr) {
        throw new Error("Invalid date format");
      }

      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-indexed
      const day = parseInt(dayStr, 10);

      return new Date(year, month, day);
    })
    .refine((date) => isAtLeast18YearsOld(date), {
      message: "You must be 18 or older to join Fitties",
    })
    .refine(
      (date) => {
        // Check maximum age of 125 years
        const age = calculateAge(date);
        return age !== null && age <= 125;
      },
      {
        message: "Please enter a valid age (maximum 125 years)",
      }
    ),
});

// Input type for the form (before transformation)
type BirthdayFormInput = z.input<typeof birthdayInputSchema>;

type BirthdayFormOutput = z.output<typeof birthdayInputSchema>;

/**
 * Birthday form component for onboarding flow.
 *
 * Displays a cute flower mascot and date picker for entering the user's birthday
 * with proper validation using React Hook Form and Zod. Includes age validation
 * (18+ years old) and handles form submission automatically.
 *
 * @example
 * ```tsx
 * <BirthdayForm />
 * ```
 */
function formatDateDefault(date: Date | undefined): string | undefined {
  if (!date) {
    return undefined;
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

interface DateOfBirthFormProps {
  onSaved?: () => void;
}

export default function DateOfBirthForm({ onSaved }: DateOfBirthFormProps) {
  const { user } = useCurrentUser();
  const { mutate: updateOnboardingUser, isPending: isSubmitting, error: mutationError } = useUpdateOnboardingUser();

  const dateDefault = formatDateDefault(user?.date_of_birth);

  const methods = useForm<BirthdayFormInput, null, BirthdayFormOutput>({
    resolver: zodResolver(birthdayInputSchema),
    ...(dateDefault ? { defaultValues: { date_of_birth: dateDefault } } : {}),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    shouldFocusError: true,
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const handleFormSubmit = (data: BirthdayFormOutput) => {
    // Transform the string date to Date object
    const date = data.date_of_birth;

    const transformedData = { date_of_birth: date };
    logger.info("📝 BirthdayForm submitting data:", transformedData);
    updateOnboardingUser(transformedData, {
      onSuccess: (result) => {
        logger.info("✅ BirthdayForm mutation successful:", result);
        onSaved?.();
      },
      onError: (error) => {
        logger.error("❌ BirthdayForm mutation failed:", error);
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <div data-testid="date-of-birth-form-root" className="h-full min-h-0 flex flex-col text-center font-mono">
        {/* Content */}
        <div data-testid="date-of-birth-form-content" className="flex-1 overflow-y-auto space-y-4 pb-2">
          {/* Header row - mascot first, then title */}
          <div className="flex items-center gap-3">
            <div data-testid="date-of-birth-form-mascot" className="shrink-0">
              <img
                data-testid="date-of-birth-form-mascot-image"
                src={flowerImage}
                alt="Cute flower mascot"
                className="w-32 h-32 object-contain"
              />
            </div>

            <h1 data-testid="date-of-birth-form-heading" className="text-2xl font-bold text-left">
              When's your birthday?
            </h1>
          </div>

          {/* Description */}
          <p data-testid="date-of-birth-form-description" className="text-black/80 text-md">
            You need to be over 18 to join Fitties
          </p>

          {/* Form */}
          <form
            data-testid="date-of-birth-form"
            onSubmit={handleSubmit(handleFormSubmit)}
            className="mx-auto w-full space-y-6"
            noValidate
          >
            {/* Server Error Message */}
            {mutationError && (
              <div data-testid="date-of-birth-form-error" className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p data-testid="date-of-birth-form-error-message" className="text-red-600 text-sm">
                  {mutationError.message}
                </p>
              </div>
            )}

            {/* Date Picker */}
            <div data-testid="date-of-birth-form-date-section">
              <Container>
                <DateOfBirthField name="date_of_birth" error={errors.date_of_birth?.message as string} />
              </Container>
            </div>
          </form>
        </div>

        {/* Button at bottom */}
        <div data-testid="date-of-birth-form-footer" className="sticky bottom-0 z-10 bg-lavender-400 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            onClick={handleSubmit(handleFormSubmit)}
            className={clsx("w-full px-8 py-4 text-lg rounded-lg font-medium transition-colors font-sans", {
              "bg-pink-600 text-white hover:bg-pink-700": !isSubmitting,
              "bg-gray-300 text-gray-500 cursor-not-allowed": isSubmitting,
            })}
            data-testid="submit-button"
          >
            <span data-testid="date-of-birth-form-submit-label">{isSubmitting ? "Saving..." : "Continue"}</span>
          </button>
        </div>
      </div>
    </FormProvider>
  );
}
